/**
 * Hook para gerenciamento de logs e estatísticas de webhooks de roteamento
 * 
 * Fornece funções para buscar logs paginados, estatísticas,
 * volume por hora e teste de webhooks.
 * 
 * @version 1.0.0
 * @date 2026-01-30
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';
import { toast } from 'sonner';
import type {
  RoutingWebhookLog,
  RoutingWebhookStats,
  RoutingWebhookVolumeData,
  PaginatedRoutingLogsResult,
} from '@/components/tenant/routing/types';

// Logger para o hook
const logger = createLogger('useRoutingWebhookLogs');

/**
 * Interface de retorno do hook
 */
export interface UseRoutingWebhookLogsReturn {
  // Logs
  logs: RoutingWebhookLog[];
  isLoadingLogs: boolean;
  logsTotal: number;
  fetchLogs: (
    ruleId: string,
    page: number,
    pageSize: number,
    filter?: string
  ) => Promise<PaginatedRoutingLogsResult>;

  // Estatísticas
  stats: RoutingWebhookStats | null;
  isLoadingStats: boolean;
  fetchStats: (ruleId: string) => Promise<void>;

  // Volume para gráfico
  volumeData: RoutingWebhookVolumeData[];
  isLoadingVolume: boolean;
  fetchVolume: (ruleId: string, hours?: number) => Promise<void>;

  // Teste
  testWebhook: (
    webhookToken: string,
    payload: Record<string, unknown>
  ) => Promise<{
    success: boolean;
    status: number;
    data: unknown;
    time: number;
  }>;
  isTesting: boolean;
}

/**
 * Hook principal para gerenciamento de logs de webhooks de roteamento
 */
export function useRoutingWebhookLogs(): UseRoutingWebhookLogsReturn {
  // Estados de logs
  const [logs, setLogs] = useState<RoutingWebhookLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsTotal, setLogsTotal] = useState(0);

  // Estados de estatísticas
  const [stats, setStats] = useState<RoutingWebhookStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Estados de volume
  const [volumeData, setVolumeData] = useState<RoutingWebhookVolumeData[]>([]);
  const [isLoadingVolume, setIsLoadingVolume] = useState(false);

  // Estado de teste
  const [isTesting, setIsTesting] = useState(false);

  /**
   * Busca logs paginados do webhook
   */
  const fetchLogs = useCallback(
    async (
      ruleId: string,
      page: number,
      pageSize: number,
      filter?: string
    ): Promise<PaginatedRoutingLogsResult> => {
      setIsLoadingLogs(true);

      try {
        const offset = (page - 1) * pageSize;
        logger.debug('Buscando logs de roteamento', { ruleId, page, pageSize, filter });

        // Buscar logs com count
        let query = supabase
          .from('routing_webhook_logs')
          .select('*', { count: 'exact' })
          .eq('rule_id', ruleId)
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);

        // Aplicar filtro de status
        if (filter && filter !== 'all') {
          query = query.eq('status', filter);
        }

        const { data, error, count } = await query;

        if (error) {
          logger.error('Erro ao buscar logs', error);
          throw error;
        }

        const result = {
          logs: (data || []) as RoutingWebhookLog[],
          totalCount: count || 0,
        };

        setLogs(result.logs);
        setLogsTotal(result.totalCount);

        return result;
      } catch (error) {
        logger.error('Erro ao buscar logs', error);
        setLogs([]);
        setLogsTotal(0);
        return { logs: [], totalCount: 0 };
      } finally {
        setIsLoadingLogs(false);
      }
    },
    []
  );

  /**
   * Busca estatísticas do webhook
   */
  const fetchStats = useCallback(async (ruleId: string): Promise<void> => {
    setIsLoadingStats(true);

    try {
      logger.debug('Buscando estatísticas', ruleId);

      // Buscar dados da regra
      const { data: rule, error: ruleError } = await supabase
        .from('routing_rules')
        .select(
          'webhook_total_requests, webhook_successful_requests, webhook_failed_requests, webhook_last_request_at'
        )
        .eq('id', ruleId)
        .single();

      if (ruleError) throw ruleError;

      // Buscar tempo médio de processamento dos logs
      const { data: avgData, error: avgError } = await supabase
        .from('routing_webhook_logs')
        .select('processing_time_ms, actions_executed')
        .eq('rule_id', ruleId)
        .limit(100);

      if (avgError) throw avgError;

      const avgProcessingTime =
        avgData && avgData.length > 0
          ? avgData.reduce((acc, log) => acc + (log.processing_time_ms || 0), 0) / avgData.length
          : 0;

      const totalActionsExecuted =
        avgData?.reduce((acc, log) => acc + (log.actions_executed || 0), 0) || 0;

      const totalRequests = rule?.webhook_total_requests || 0;
      const successfulRequests = rule?.webhook_successful_requests || 0;

      setStats({
        totalRequests,
        successfulRequests,
        failedRequests: rule?.webhook_failed_requests || 0,
        successRate: totalRequests > 0 ? (successfulRequests / totalRequests) * 100 : 0,
        lastRequestAt: rule?.webhook_last_request_at || null,
        avgProcessingTime: Math.round(avgProcessingTime),
        totalActionsExecuted,
      });
    } catch (error) {
      logger.error('Erro ao buscar estatísticas', error);
      setStats(null);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  /**
   * Busca volume por hora para gráfico
   */
  const fetchVolume = useCallback(
    async (ruleId: string, hours: number = 24): Promise<void> => {
      setIsLoadingVolume(true);

      try {
        logger.debug('Buscando volume por hora', { ruleId, hours });

        // Calcular data de início
        const startDate = new Date();
        startDate.setHours(startDate.getHours() - hours);

        // Buscar logs agrupados por hora
        const { data, error } = await supabase
          .from('routing_webhook_logs')
          .select('created_at, status')
          .eq('rule_id', ruleId)
          .gte('created_at', startDate.toISOString())
          .order('created_at', { ascending: true });

        if (error) throw error;

        // Agrupar por hora
        const hourMap = new Map<number, { total: number; sucesso: number; erro: number }>();

        // Inicializar todas as horas com zero
        for (let i = 0; i < hours; i++) {
          const date = new Date();
          date.setHours(date.getHours() - (hours - 1 - i));
          const hour = date.getHours();
          hourMap.set(hour, { total: 0, sucesso: 0, erro: 0 });
        }

        // Preencher com dados reais
        data?.forEach((log) => {
          const logDate = new Date(log.created_at);
          const hour = logDate.getHours();
          const current = hourMap.get(hour) || { total: 0, sucesso: 0, erro: 0 };
          current.total++;
          if (log.status === 'success') {
            current.sucesso++;
          } else {
            current.erro++;
          }
          hourMap.set(hour, current);
        });

        // Converter para array ordenado
        const volumeArray: RoutingWebhookVolumeData[] = [];
        const now = new Date();

        for (let i = 0; i < hours; i++) {
          const date = new Date();
          date.setHours(now.getHours() - (hours - 1 - i));
          const hour = date.getHours();
          const hourData = hourMap.get(hour) || { total: 0, sucesso: 0, erro: 0 };

          volumeArray.push({
            hora: hour,
            data_hora: date.toISOString(),
            total: hourData.total,
            sucesso: hourData.sucesso,
            erro: hourData.erro,
          });
        }

        setVolumeData(volumeArray);
      } catch (error) {
        logger.error('Erro ao buscar volume', error);
        setVolumeData([]);
      } finally {
        setIsLoadingVolume(false);
      }
    },
    []
  );

  /**
   * Testa o webhook com um payload
   */
  const testWebhook = useCallback(
    async (
      webhookToken: string,
      payload: Record<string, unknown>
    ): Promise<{
      success: boolean;
      status: number;
      data: unknown;
      time: number;
    }> => {
      setIsTesting(true);

      try {
        logger.info('Testando webhook de roteamento', webhookToken.substring(0, 8));

        // Construir URL usando helper centralizado (mesmo padrão do CRM)
        // Encode do token para preservar caracteres especiais (+, =, etc.)
        const webhookUrl = `${getEdgeFunctionUrl('routing-webhook')}?token=${encodeURIComponent(webhookToken)}`;

        const startTime = performance.now();

        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const endTime = performance.now();
        const time = Math.round(endTime - startTime);

        const data = await response.json();

        const result = {
          success: response.ok,
          status: response.status,
          data,
          time,
        };

        if (result.success) {
          toast.success(`Teste bem-sucedido em ${time}ms`);
        } else {
          toast.error(`Erro no teste: ${data.error || 'Erro desconhecido'}`);
        }

        return result;
      } catch (error) {
        logger.error('Erro ao testar webhook', error);
        toast.error('Erro ao conectar com o webhook');
        return {
          success: false,
          status: 0,
          data: { error: 'Erro de conexão' },
          time: 0,
        };
      } finally {
        setIsTesting(false);
      }
    },
    []
  );

  return {
    // Logs
    logs,
    isLoadingLogs,
    logsTotal,
    fetchLogs,

    // Estatísticas
    stats,
    isLoadingStats,
    fetchStats,

    // Volume
    volumeData,
    isLoadingVolume,
    fetchVolume,

    // Teste
    testWebhook,
    isTesting,
  };
}
